/**
 * Le tarif d'un enfant sur plusieurs semaines, en un aller-retour.
 *
 * ## Pourquoi ce fichier existe
 *
 * Deux endroits du dépôt réimplémentaient la règle du tarif local :
 * `activityStore.getPrice()` et `adminRegistrationStore.fetchAvailableSessions()`.
 * Deux copies de la même règle, et aucune des deux ne testait
 * `tarif_conditions.active` — le prix dépendait donc de QUI regardait, la RLS
 * cachant les conditions inactives à un parent et les montrant à un
 * administrateur.
 *
 * `resolve_prices_for_kid` n'a aucune règle propre : elle boucle sur
 * `resolve_registration_price`, la fonction que la facture utilise. Le
 * catalogue et la facture ne peuvent donc plus se contredire.
 */

import { supabase } from '../supabase';
import { ErreurDeCommande } from './gateway.supabase';
import type { TypeDeTarif } from './devis';

export interface TarifResolu {
  session_id: string;
  price_type: TypeDeTarif;
  /** Ce qui sera facturé, en centimes. */
  amount_cents: number;
  /** Le tarif de base de la même semaine, pour afficher « au lieu de ». */
  full_amount_cents: number;
  local_applied: boolean;
  reduced_applied: boolean;
  /** Pourquoi le tarif local s'applique : 'code_postal', 'ecole', ou null. */
  local_reason: 'code_postal' | 'ecole' | null;
}

/**
 * Rend une entrée par session demandée, indexée par `session_id`.
 *
 * Un tableau vide rend une Map vide sans aller-retour : un catalogue encore en
 * chargement passe par là, et un appel pour rien coûte une latence visible.
 */
export async function tarifsPourEnfant(
  kidId: string,
  sessionIds: string[],
  reduitDeclare = false,
): Promise<Map<string, TarifResolu>> {
  if (!kidId || sessionIds.length === 0) return new Map();

  const { data, error } = await supabase.rpc('resolve_prices_for_kid', {
    p_kid_id: kidId,
    p_session_ids: sessionIds,
    p_reduced_declared: reduitDeclare,
  });

  if (error) throw new ErreurDeCommande(error.message);

  const m = new Map<string, TarifResolu>();
  (data as TarifResolu[] | null)?.forEach((t) => m.set(t.session_id, t));
  return m;
}
